"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useClickOutsideHandler } from '@/hooks/useClickOutsideHandler';
import { motion, AnimatePresence } from 'framer-motion';
// Delivery calculation helpers (to be implemented)
// import { calculateDeliveryFee } from '@/lib/delivery';
import { getStorePaymentCredential } from '@/lib/supabase/storePaymentCredentials';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Plus, Check, X, CreditCard, WalletCards, Truck, Car, Bike, Motorbike, MapPin, Camera, Image as ImageIcon, ShoppingCart, ArrowRight } from '@/components/icons';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/lib/supabase/client';
import { formatCurrency, formatAddressForDisplay } from '@/lib/utils';
import { cn } from '@/lib/utils';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { useScrollLock } from '@/hooks/useScrollLock';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getCartForUser, updateCartNotes } from '@/lib/supabase/carts';
import { usePullToClose } from '@/hooks/usePullToClose';
import { useAddressManager } from '@/context/AddressManagerContext';
import { getUserAddresses } from '@/lib/supabase/userAddresses';
import { getStoreAddresses } from '@/lib/supabase/storeAddresses';
import { calculateDeliveryFee } from '@/lib/delivery';

export interface BankAccount {
  cbu: string;
  bank_name: string;
  account_holder: string;
  alias?: string;
  primary?: boolean;
}

interface PaymentSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    buyerCbu: string,
    buyerBankName: string,
    buyerAccountHolder?: string,
    orderNotes?: string,
    paymentMethod?: string,
    deliveryData?: {
      delivery_fee?: number | null;
      delivery_distance?: number | null;
      delivery_pickup_address?: string | null;
      delivery_address?: string | null;
    },
    paymentProofUrl?: string | null
  ) => Promise<void>;
  storeId: string;
  storeName: string;
  total: number;
  cartId?: string;
  buyerProfile: {
    bank_cbu?: string;
    bank_name?: string;
    bank_account_holder?: string;
    bank_accounts?: BankAccount[];
  } | null;
  userId: string;
  isEmbedded?: boolean;
  isDigital?: boolean;
  userEmail?: string;
  userPhoneNumber?: string;
}

const paymentPlatforms = [
  { name: 'Pago por transferencia', key: 'bank_transfer', color: 'hsl(var(--bank-transfer))' },
  { name: 'MercadoPago', key: 'mercadopago', color: 'hsl(var(--mercadopago))' },
  { name: 'Ualá', key: 'uala', color: 'hsl(var(--uala))' },
  { name: 'Naranja X', key: 'naranjax', color: 'hsl(var(--naranja))' },
  { name: 'Mobbex', key: 'mobbex', color: 'hsl(var(--mobbex))' },
];

const PAYMENT_CONFIRM_SLIDER_MIN_WIDTH = 120;
const PAYMENT_CONFIRM_SLIDER_MAX_WIDTH = 180;
const PAYMENT_CONFIRM_SLIDER_WIDTH_RATIO = 0.48;
const PAYMENT_CONFIRM_SLIDER_HANDLE_WIDTH_CSS = 'clamp(120px, 48%, 180px)';
const PAYMENT_CONFIRM_SLIDER_TRACK_INSET = 1;
const PAYMENT_CONFIRM_SLIDER_ACTIVATE_RATIO = 0.9;
const PAYMENT_CONFIRM_SLIDER_HANDLE_SNAP_MS = 420;
const PAYMENT_CONFIRM_SLIDER_HANDLE_SNAP_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const PAYMENT_CONFIRM_SLIDER_FILL_SNAP_MS = 180;
const PAYMENT_CONFIRM_SLIDER_FILL_SNAP_EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

export const PaymentSidebar: React.FC<PaymentSidebarProps> = ({
  isOpen,
  onClose,
  onConfirm,
  storeId,
  storeName,
  total,
  cartId,
  buyerProfile,
  userId,
  isEmbedded = false,
  isDigital = false,
  userEmail,
  userPhoneNumber,
}) => {
  const MINIBID_MAX_LIGHT_DELIVERY_KM = 20;
  const normalizeCityId = (city: string) => city
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');

  const getCurrentCityId = (): string => {
    try {
      const raw = localStorage.getItem('relevant_location');
      if (raw) {
        const parsed = JSON.parse(raw);
        const city = String(parsed?.city || '').trim();
        if (city) return normalizeCityId(city);
      }
    } catch {
      // ignore
    }
    return normalizeCityId('Salta');
  };

  const [isClosing, setIsClosing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const receiptInputRef = useRef<HTMLInputElement>(null);
  const cameraButtonRef = useRef<HTMLButtonElement>(null);
  const galleryButtonRef = useRef<HTMLButtonElement>(null);
  const [proofPhotoFile, setProofPhotoFile] = useState<File | null>(null);
  const [proofPhotoUrl, setProofPhotoUrl] = useState<string | null>(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [confirmSliderX, setConfirmSliderX] = useState(0);
  const [isInteractingWithConfirmSlider, setIsInteractingWithConfirmSlider] = useState(false);
  const { headerHeight, isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();
  const { toast } = useToast();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const confirmSliderTrackRef = useRef<HTMLDivElement>(null);
  const confirmSliderHandleRef = useRef<HTMLDivElement>(null);
  const confirmSliderDragRef = useRef<{ pointerId: number | null; startClientX: number; startSliderX: number; maxX: number }>({
    pointerId: null,
    startClientX: 0,
    startSliderX: 0,
    maxX: 0,
  });

  useScrollLock(isOpen || isClosing, false, scrollAreaRef);
  
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  }, [onClose]);

  // Hook reutilizable para manejar clicks fuera de la sidebar
  useClickOutsideHandler({
    isOpen,
    sidebarRef,
    handleClose,
    // No necesitamos ignoreRefs aquí a menos que haya sidebars secundarias que no deban cerrar ésta
  });

  // Activar pull-to-close (solo en móvil)
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: isOpen && !isDesktop
  });

  // Cerrar con tecla ESC (only if not embedded)
  useEffect(() => {
    if (isEmbedded) return;
    
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isEmbedded, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    setProofPhotoFile(null);
    setProofPhotoUrl(null);
    setUploadingProof(false);
    setOrderNotes('');
    if (receiptInputRef.current) {
      receiptInputRef.current.value = '';
    }
    setPreviousOrderNoteOffset(0);
  }, [isOpen, storeId, userId, cartId]);

  const [enabledPaymentMethods, setEnabledPaymentMethods] = useState<string[]>(['bank_transfer']);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('bank_transfer');
  
  const [orderNotes, setOrderNotes] = useState("");
  const [previousOrderNoteOffset, setPreviousOrderNoteOffset] = useState(0);
  const [isLoadingPreviousOrderNote, setIsLoadingPreviousOrderNote] = useState(false);
  
  // Check if this is minibid store
  const isMinibid = storeName?.toLowerCase().includes('minibid');
  
  // Delivery state
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [arrangeWithSeller, setArrangeWithSeller] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState<number | null>(null);
  const [transportType, setTransportType] = useState<'auto' | 'moto' | 'bici' | 'van' | 'light'>('light');
  // Initialize with default transport options so they show immediately
  const defaultTransportOptions = [
    // DOCUMENTATION: Changed to show only Bike option - hiding auto and van for bike-only delivery
    // Changed label from 'Liviano' to 'Bici' and icon from Motorbike to Bike
    { type: 'light' as const, label: 'Bici', icon: Bike, fee: 0, isLoading: true },
    // { type: 'auto' as const, label: 'Auto', icon: Car, fee: 0, isLoading: true },
    // { type: 'van' as const, label: 'Camioneta', icon: Truck, fee: 0, isLoading: true },
  ];
  const [transportOptions, setTransportOptions] = useState<Array<{ type: 'auto' | 'moto' | 'bici' | 'van' | 'light'; label: string; icon: React.ElementType; fee: number; isLoading?: boolean }>>(defaultTransportOptions);
  const [cartSubtotal, setCartSubtotal] = useState<number | null>(null);
  const [isDeliveryCalculated, setIsDeliveryCalculated] = useState(false);
  const [isFreeShipping, setIsFreeShipping] = useState(false);
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [deliveryRouteInfo, setDeliveryRouteInfo] = useState<{ from: string; to: string } | null>(null);
  const [driversCityId, setDriversCityId] = useState<string>(getCurrentCityId);
  const [onlineDriversCount, setOnlineDriversCount] = useState<number | null>(null);
  const isMinibidDeliveryUnavailableByDrivers = isMinibid && onlineDriversCount !== null && onlineDriversCount <= 0;
  const isMinibidLightUnavailableByDistance = isMinibid && deliveryDistance !== null && deliveryDistance > MINIBID_MAX_LIGHT_DELIVERY_KM;
  const isMinibidDeliveryUnavailable = isMinibidDeliveryUnavailableByDrivers;
  // NUEVO: coordenadas pickup y delivery
  const [deliveryPickupCoords, setDeliveryPickupCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [deliveryCoords, setDeliveryCoords] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    const handleLocationUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{ city?: string }>;
      const city = String(customEvent.detail?.city || '').trim();
      if (!city) return;
      setDriversCityId(normalizeCityId(city));
    };

    window.addEventListener('location-updated', handleLocationUpdated as EventListener);
    return () => {
      window.removeEventListener('location-updated', handleLocationUpdated as EventListener);
    };
  }, []);

  const refreshOnlineDriversCount = useCallback(async () => {
    if (!isMinibid || !driversCityId) {
      setOnlineDriversCount(null);
      return;
    }

    try {
      const { count, error } = await supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .eq('is_online', true)
        .eq('city_id', driversCityId)
        .in('service_type', ['courier', 'driver']);

      if (error) {
        console.warn('[PaymentSidebar] Could not load online drivers count:', error);
        setOnlineDriversCount(0);
        return;
      }

      setOnlineDriversCount(count || 0);
    } catch (error) {
      console.warn('[PaymentSidebar] Failed to refresh online drivers count:', error);
      setOnlineDriversCount(0);
    }
  }, [isMinibid, driversCityId]);

  useEffect(() => {
    if (!isOpen || !isMinibid) return;

    void refreshOnlineDriversCount();

    const intervalId = window.setInterval(() => {
      void refreshOnlineDriversCount();
    }, 45000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isOpen, isMinibid, refreshOnlineDriversCount]);

  useEffect(() => {
    if (!isOpen || !isMinibid || !driversCityId) return;

    const channel = supabase
      .channel(`payment-online-drivers-${driversCityId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `city_id=eq.${driversCityId}`,
        },
        () => {
          void refreshOnlineDriversCount();
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [isOpen, isMinibid, driversCityId, refreshOnlineDriversCount]);

  // Force delivery on for minibid (hide pickup option completely)
  useEffect(() => {
    if (isMinibid) {
      setDeliveryEnabled(true);
      setArrangeWithSeller(false);
      setTransportType('light');
    }
  }, [isMinibid]);

  useEffect(() => {
    if (!isMinibid || !isMinibidDeliveryUnavailableByDrivers) return;
    if (!deliveryEnabled) return;
    setDeliveryEnabled(false);
    setArrangeWithSeller(true);
  }, [isMinibid, isMinibidDeliveryUnavailableByDrivers, deliveryEnabled]);

  useEffect(() => {
    if (!isMinibidLightUnavailableByDistance) return;
    if (transportType !== 'light') return;
    const fallbackOption = transportOptions.find((o) => o.type === 'auto' || o.type === 'van');
    if (fallbackOption) {
      setTransportType(fallbackOption.type);
      setDeliveryFee(fallbackOption.fee);
    }
  }, [isMinibidLightUnavailableByDistance, transportType, transportOptions]);

  // Dirección state
  const [userAddresses, setUserAddresses] = useState<any[]>([]);
  const { open: openAddressManager } = useAddressManager();

  useEffect(() => {
    if (!isOpen || !storeId || !userId) {
      return;
    }
    setDeliveryLoading(true);
    setDeliveryError(null);
    (async () => {
      try {
        // Fetch store primary address
        const storeAddresses = await getStoreAddresses(storeId);
        const storeAddress = storeAddresses.find(a => a.is_primary) || storeAddresses[0];
        // Fetch user address
        const addresses = await getUserAddresses(userId);
        const userAddress = addresses.find(a => a.is_primary) || addresses[0];
        console.debug('[Delivery] Store address:', storeAddress);
        console.debug('[Delivery] User address:', userAddress);
        if (!storeAddress || !storeAddress.location) throw new Error('La tienda no tiene ubicación configurada');
        if (!userAddress || !userAddress.location) throw new Error('No se encontró dirección de entrega');

        // Calculate delivery fee using OpenRouteService
        const origin = { lat: storeAddress.location.latitude, lng: storeAddress.location.longitude };
        const destination = { lat: userAddress.location.latitude, lng: userAddress.location.longitude };
        setDeliveryPickupCoords(origin);
        setDeliveryCoords(destination);
        console.debug('[Delivery] Origin coordinates:', origin);
        console.debug('[Delivery] Destination coordinates:', destination);
        try {
          // Obtener subtotal del canasto y calcular usando servicio 'paquete' (coincide con PromoBuyerActions)
          let subtotal: number | null = null;
          try {
            const cart = await getCartForUser(userId, storeId);
            if (cart && cart.cart_items && Array.isArray(cart.cart_items)) {
              subtotal = cart.cart_items.reduce((s: number, it: any) => s + (Number(it.unit_price || 0) * Number(it.quantity || 0)), 0);
            }
          } catch (e) {
            console.warn('[Delivery] could not fetch cart for subtotal, proceeding without subtotal', e);
          }

          // Save subtotal for minibid logic
          setCartSubtotal(subtotal);

          // Calcular opciones de transporte
          const transportTypes: Array<'auto' | 'moto' | 'bici' | 'van' | 'light'> = ['light', 'auto', 'van'];
          const options = [];
          const icons: { [key: string]: React.ElementType } = { 
            bici: Bike, 
            moto: Motorbike, 
            light: Motorbike,
            auto: Car, 
            van: Truck 
          };
          
          for (const tType of transportTypes) {
            try {
              // For 'light', use 'moto' to calculate since light groups bike+moto with same price
              const calcType = tType === 'light' ? 'moto' : tType;
              const result = await calculateDeliveryFee(origin, destination, calcType, 'paquete', subtotal);
              // DOCUMENTATION: Changed 'Liviano' to 'Bici' for bike-only delivery
              const labels = { bici: 'Bici', moto: 'Moto', auto: 'Auto', light: 'Bici', van: 'Camioneta' };
              options.push({
                type: tType,
                label: labels[tType],
                icon: icons[tType],
                fee: result.fee,
                isLoading: false
              });
            } catch (err) {
              console.warn(`[Delivery] Could not calculate fee for ${tType}`, err);
            }
          }
          
          setTransportOptions(options);
          
          // Determine default transport and fee
          let selectedFee: number | null = null;
          let selectedType: 'auto' | 'moto' | 'bici' | 'van' | 'light' = 'light';
          const lightOption = options.find(o => o.type === 'light');
          const autoOption = options.find(o => o.type === 'auto');
          const vanOption = options.find(o => o.type === 'van');

          if (isMinibid) {
            if (lightOption) {
              selectedType = 'light';
              selectedFee = lightOption.fee;
            } else if (autoOption) {
              selectedType = 'auto';
              selectedFee = autoOption.fee;
            } else if (vanOption) {
              selectedType = 'van';
              selectedFee = vanOption.fee;
            }
          } else {
            if (lightOption) {
              selectedType = 'light';
              selectedFee = lightOption.fee;
            } else if (autoOption) {
              selectedType = 'auto';
              selectedFee = autoOption.fee;
            } else if (vanOption) {
              selectedType = 'van';
              selectedFee = vanOption.fee;
            }
          }

          const result = await calculateDeliveryFee(origin, destination, 'auto', 'paquete', subtotal);
          console.debug('[Delivery] OpenRouteService result:', result);
          setDeliveryDistance(result.distanceKm);

          if (isMinibid && result.distanceKm > MINIBID_MAX_LIGHT_DELIVERY_KM && selectedType === 'light') {
            const fallbackOption = autoOption || vanOption;
            if (fallbackOption) {
              selectedType = fallbackOption.type;
              selectedFee = fallbackOption.fee;
            }
          }

          setTransportType(selectedType);
          if (selectedFee !== null) {
            setDeliveryFee(selectedFee);
          }
          setIsFreeShipping(selectedFee === 0);
          setIsDeliveryCalculated(true);
            const getFullAddress = (addressLike: any) => {
              const fromLabel = String(addressLike?.label || '').trim();
              const fromDisplay = String(addressLike?.location?.displayName || addressLike?.displayName || '').trim();
              return formatAddressForDisplay(fromLabel || fromDisplay);
            };
            setDeliveryRouteInfo({
              from: getFullAddress(storeAddress),
              to: getFullAddress(userAddress)
            });
        } catch (apiError) {
          console.error('[Delivery] OpenRouteService error:', apiError);
          throw apiError;
        }
      } catch (e: any) {
        setDeliveryError(e?.message || 'No se pudo calcular el envío');
        setDeliveryEnabled(false);
      } finally {
        setDeliveryLoading(false);
      }
    })();
  }, [isOpen, storeId, userId, isMinibid, transportType]);

  // Recargar direcciones cuando el sidebar está abierto
  useEffect(() => {
    if (!isOpen || !userId) return;
    
    const loadAddresses = async () => {
      try {
        const addresses = await getUserAddresses(userId);
        setUserAddresses(addresses || []);
      } catch (err) {
        console.error('[PaymentSidebar] Error loading addresses:', err);
      }
    };
    
    loadAddresses();
  }, [isOpen, userId]);

  // Recalcular delivery cuando cambia la dirección primaria (solamente)
  useEffect(() => {
    if (!deliveryPickupCoords || !userId || !storeId) return;
    
    const handleRecalculate = async () => {
      try {
        // Obtener dirección primaria actual
        const addresses = await getUserAddresses(userId);
        const primaryAddress = addresses.find(a => a.is_primary);
        
        if (!primaryAddress?.location) return;
        
        // Verificar si es diferente de la actual
        if (deliveryCoords?.lat === primaryAddress.location.latitude && 
            deliveryCoords?.lng === primaryAddress.location.longitude) {
          return; // No cambió, salir
        }
        
        setDeliveryLoading(true);
        setDeliveryError(null);
        
        const destination = {
          lat: primaryAddress.location.latitude,
          lng: primaryAddress.location.longitude
        };
        
        setDeliveryCoords(destination);
        
        // Recalcular delivery fee
        let subtotal: number | null = null;
        try {
          const cart = await getCartForUser(userId, storeId);
          if (cart?.cart_items?.length) {
            subtotal = cart.cart_items.reduce((s: any, it: any) => s + (Number(it.unit_price) * Number(it.quantity)), 0);
          }
        } catch (e) {
          console.warn('[Delivery] Could not fetch cart');
        }

        const transportTypes: Array<'auto' | 'moto' | 'bici' | 'van' | 'light'> = ['light', 'auto', 'van'];
        const options = [];
        const icons: { [key: string]: React.ElementType } = {
          bici: Bike,
          moto: Motorbike,
          light: Motorbike,
          auto: Car,
          van: Truck
        };
        
        for (const tType of transportTypes) {
          try {
            const calcType = tType === 'light' ? 'moto' : tType;
            const result = await calculateDeliveryFee(deliveryPickupCoords, destination, calcType, 'paquete', subtotal);
            // DOCUMENTATION: Changed 'Liviano' to 'Bici' for bike-only delivery
            const labels = { bici: 'Bici', moto: 'Moto', auto: 'Auto', light: 'Bici', van: 'Camioneta' };
            options.push({
              type: tType,
              label: labels[tType],
              icon: icons[tType],
              fee: result.fee,
              isLoading: false
            });
          } catch (err) {
            console.warn(`[Delivery] Fee calc error for ${tType}`);
          }
        }
        
        setTransportOptions(options);

        let fee: number | null = null;
        let selectedType: 'auto' | 'moto' | 'bici' | 'van' | 'light' = 'light';
        const lightOption = options.find(o => o.type === 'light');
        const autoOption = options.find(o => o.type === 'auto');
        const vanOption = options.find(o => o.type === 'van');

        if (isMinibid) {
          if (lightOption) {
            selectedType = 'light';
            fee = lightOption.fee;
          } else if (autoOption) {
            selectedType = 'auto';
            fee = autoOption.fee;
          } else if (vanOption) {
            selectedType = 'van';
            fee = vanOption.fee;
          }
        } else {
          if (lightOption) {
            selectedType = 'light';
            fee = lightOption.fee;
          } else if (autoOption) {
            selectedType = 'auto';
            fee = autoOption.fee;
          } else if (vanOption) {
            selectedType = 'van';
            fee = vanOption.fee;
          }
        }

        try {
          const autoResult = await calculateDeliveryFee(deliveryPickupCoords, destination, 'auto', 'paquete', subtotal);
          setDeliveryDistance(autoResult.distanceKm);
          if (isMinibid && autoResult.distanceKm > MINIBID_MAX_LIGHT_DELIVERY_KM && selectedType === 'light') {
            const fallbackOption = autoOption || vanOption;
            if (fallbackOption) {
              selectedType = fallbackOption.type;
              fee = fallbackOption.fee;
            }
          }
        } catch {
          // Ignore distance refresh errors on address updates; fee options were already recalculated.
        }

        setTransportType(selectedType);
        if (fee !== null) {
          setDeliveryFee(fee);
        }
        setIsFreeShipping(fee === 0);

        // Actualizar ruta con la direccion completa canónica
        const getFullAddress = (addressLike: any) => {
          const fromLabel = String(addressLike?.label || '').trim();
          const fromDisplay = String(addressLike?.location?.displayName || addressLike?.displayName || '').trim();
          return formatAddressForDisplay(fromLabel || fromDisplay);
        };
        
        setDeliveryRouteInfo({
          from: deliveryRouteInfo?.from || 'Tienda',
          to: getFullAddress(primaryAddress)
        });
        
        toast({
          title: '✅ Dirección actualizada',
          description: 'Se recalculó el costo de envío',
        });
      } catch (err: any) {
        console.error('[PaymentSidebar] Error recalculating:', err);
      } finally {
        setDeliveryLoading(false);
      }
    };
    
    handleRecalculate();
  }, [deliveryPickupCoords, storeId, userId, toast, deliveryRouteInfo, deliveryCoords, isMinibid]);

  // Actualizar isFreeShipping cuando el fee cambia (para todas las tiendas)
  useEffect(() => {
    if (deliveryFee !== null) {
      setIsFreeShipping(deliveryFee === 0);
    }
  }, [deliveryFee]);

  const [mpLoading, setMpLoading] = useState(false);
  const [mpError, setMpError] = useState<string | null>(null);
  const [mpInitPoint, setMpInitPoint] = useState<string | null>(null);
  const mpInitializedRef = useRef(false);
  // Ualá
  const [ualaLoading, setUalaLoading] = useState(false);
  const [ualaError, setUalaError] = useState<string | null>(null);
  const [ualaPaymentLink, setUalaPaymentLink] = useState<string | null>(null);
    // Ualá: crear preferencia y obtener link
    const handleUala = useCallback(async () => {
      setUalaLoading(true);
      setUalaError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('No autenticado');
        const cart = await getCartForUser(userId, storeId);
        if (!cart) throw new Error('Canasto no encontrado');
        const items = [{
          title: `Pedido en ${storeName}`,
          quantity: 1,
          unit_price: Number(total),
          currency_id: 'ARS',
        }];
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://xflhwnmwfbtjdanubjqk.supabase.co';
        
        
        const res = await fetch(`${supabaseUrl}/functions/v1/uala-create-preference`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            storeId,
            items,
            userId,
            cartId: cart.id,
            notes: orderNotes || null
          }),
        });
        
        
        const data = await res.json();
        
        
        if (!data?.paymentLink) {
          console.error('[PaymentSidebar] Ualá response error - Full data:', data);
          throw new Error(data?.error || 'No se pudo crear la preferencia de Ualá');
        }
        
        setUalaPaymentLink(data.paymentLink);
      } catch (err: any) {
        const errorMsg = err?.message || 'Error al iniciar Ualá';
        console.error('[PaymentSidebar] Ualá error:', {
          message: errorMsg,
          error: err,
          stack: err?.stack
        });
        setUalaError(errorMsg);
      } finally {
        setUalaLoading(false);
      }
    }, [storeId, storeName, total, userId, orderNotes]);
  
  const [loading, setLoading] = useState(false);
  const [loadingStore, setLoadingStore] = useState(true);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [storeBankAccounts, setStoreBankAccounts] = useState<BankAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  
  const [buyerBankAccounts, setBuyerBankAccounts] = useState<BankAccount[]>([]);
  const [selectedBuyerAccount, setSelectedBuyerAccount] = useState<BankAccount | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [buyerCbu, setBuyerCbu] = useState('');
  const [buyerBankName, setBuyerBankName] = useState('');
  const [buyerAccountHolder, setBuyerAccountHolder] = useState('');
  const [saveBuyerInfo, setSaveBuyerInfo] = useState(true);
  // Eliminada declaración duplicada de orderNotes
  
  const handleMercadoPago = useCallback(async () => {
    if (mpInitializedRef.current) return;
    mpInitializedRef.current = true;
    
    setMpLoading(true);
    setMpError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No autenticado');
      
      // Obtener el cart_id actual
      const cart = await getCartForUser(userId, storeId);
      if (!cart) throw new Error('Canasto no encontrado');
      
      const items = [{
        title: `Pedido en ${storeName}`,
        quantity: 1,
        unit_price: Number(total),
        currency_id: 'ARS',
      }];
      
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://xflhwnmwfbtjdanubjqk.supabase.co';
      const res = await fetch(`${supabaseUrl}/functions/v1/mercadopago-create-preference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ 
          storeId, 
          items,
          userId,
          cartId: cart.id,
          notes: orderNotes || null
        }),
      });
      
      const data = await res.json();
      
      
      if (!data?.preferenceId || !data?.publicKey) {
        console.error('[PaymentSidebar] Full response:', data);
        throw new Error(data?.error || 'No se pudo crear la preferencia de MercadoPago');
      }
      
      setMpInitPoint(data.init_point);
    } catch (err: any) {
      setMpError(err?.message || 'Error al iniciar MercadoPago');
      mpInitializedRef.current = false;
    } finally {
      setMpLoading(false);
    }
  }, [storeId, storeName, total, userId, orderNotes]);
  
  useEffect(() => {
    if (!isOpen || !storeId) return;
    
    let mounted = true;
    const loadData = async () => {
      setLoadingStore(true);
      try {
        const { data, error } = await supabase
          .from('stores')
          .select('bank_accounts')
          .eq('id', storeId)
          .single();
        
        if (!mounted) return;
        
        if (error && error.code !== 'PGRST204' && !error.message?.includes('bank_accounts')) {
          throw error;
        }
        
        const accounts = (data?.bank_accounts as BankAccount[]) || [];
        setStoreBankAccounts(accounts);
        if (accounts.length > 0) {
          const primary = accounts.find(a => a.primary);
          setSelectedAccount(primary || accounts[0]);
        }
      } catch (e: any) {
        console.error('Error loading store bank accounts:', e);
      } finally {
        if (mounted) setLoadingStore(false);
      }
    };
    
    loadData();
    return () => { mounted = false; };
  }, [isOpen, storeId]);
  
  useEffect(() => {
    if (!isOpen || !storeId) return;
    
    let mounted = true;
    const loadPaymentMethods = async () => {
      if (mounted) setLoadingPaymentMethods(true);
      const enabled: string[] = ['bank_transfer'];
      for (const platform of paymentPlatforms) {
        if (platform.key === 'bank_transfer') continue;
        try {
          const cred = await getStorePaymentCredential(storeId, platform.key);
          if (cred && mounted) enabled.push(platform.key);
        } catch (err) {
          console.error(`Error verificando ${platform.key}:`, err);
        }
      }
      if (mounted) {
        setEnabledPaymentMethods(enabled);
        setSelectedPaymentMethod('bank_transfer');
        setLoadingPaymentMethods(false);
      }
    };
    
    loadPaymentMethods();
    return () => { mounted = false; };
  }, [isOpen, storeId]);
  
  useEffect(() => {
    if (!isOpen || !userId || !storeId) return;
    
    let mounted = true;
    const loadNotes = async () => {
      try {
        const cart = cartId
          ? await supabase
              .from('carts')
              .select('notes')
              .eq('id', cartId)
              .maybeSingle()
              .then((result) => result.data)
          : await getCartForUser(userId, storeId);

        if (!mounted) return;
        if (cart?.notes) {
          // Si las notas parecen ser un JSON de sistema (itinerario de tours), no las mostramos al usuario
          if (cart.notes.startsWith('{"type":"tour_')) {
            setOrderNotes('');
            return;
          }
          setOrderNotes(cart.notes);
          return;
        }
        setOrderNotes('');
      } catch (e) {
        console.error('Error loading cart notes:', e);
        if (mounted) setOrderNotes('');
      }
    };
    
    loadNotes();
    return () => { mounted = false; };
  }, [isOpen, userId, storeId, cartId]);

  const handlePastePreviousOrderNote = useCallback(async () => {
    if (!userId || !storeId || isLoadingPreviousOrderNote) return;

    setIsLoadingPreviousOrderNote(true);

    try {
      let offset = previousOrderNoteOffset;
      let wrapped = false;

      for (let attempts = 0; attempts < 120; attempts += 1) {
        const { data, error } = await supabase
          .from('orders')
          .select('notes')
          .eq('user_id', userId)
          .eq('store_id', storeId)
          .order('created_at', { ascending: false })
          .range(offset, offset);

        if (error) {
          console.error('Error loading previous order note:', error);
          toast({
            title: 'Error',
            description: 'No pudimos cargar notas anteriores en este momento.',
            variant: 'destructive',
          });
          return;
        }

        const row = (data || [])[0] as any;
        if (!row) {
          if (wrapped) {
            toast({
              title: 'Sin notas previas',
              description: 'No encontramos notas anteriores para esta tienda.',
            });
            setPreviousOrderNoteOffset(0);
            return;
          }

          wrapped = true;
          offset = 0;
          continue;
        }

        const candidate = String(row?.notes || '').trim();
        offset += 1;

        if (!candidate || candidate.startsWith('{"type":"tour_')) {
          continue;
        }

        setOrderNotes(candidate);
        setPreviousOrderNoteOffset(offset);
        return;
      }

      setPreviousOrderNoteOffset(0);
      toast({
        title: 'Sin notas previas',
        description: 'No encontramos notas anteriores para esta tienda.',
      });
    } finally {
      setIsLoadingPreviousOrderNote(false);
    }
  }, [userId, storeId, previousOrderNoteOffset, isLoadingPreviousOrderNote, toast]);
  
  useEffect(() => {
    if (!userId || !storeId || !orderNotes) return;
    
    const timeoutId = setTimeout(async () => {
      try {
        await updateCartNotes(userId, storeId, orderNotes);
      } catch (e) {
        console.error('Error saving cart notes:', e);
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [orderNotes, userId, storeId]);
  
  useEffect(() => {
    if (!buyerProfile) {
      setBuyerBankAccounts([]);
      setSelectedBuyerAccount(null);
      setShowAddForm(true);
      return;
    }
    
    const accounts = (buyerProfile.bank_accounts as BankAccount[]) || [];
    
    if (accounts.length === 0 && buyerProfile.bank_cbu) {
      const legacyAccount: BankAccount = {
        cbu: buyerProfile.bank_cbu,
        bank_name: buyerProfile.bank_name || '',
        account_holder: buyerProfile.bank_account_holder || '',
        primary: true
      };
      setBuyerBankAccounts([legacyAccount]);
      setSelectedBuyerAccount(legacyAccount);
      setShowAddForm(false);
    } else {
      setBuyerBankAccounts(accounts);
      const primary = accounts.find(a => a.primary);
      setSelectedBuyerAccount(primary || accounts[0] || null);
      setShowAddForm(accounts.length === 0);
    }
  }, [buyerProfile]);
  
  useEffect(() => {
    if (selectedPaymentMethod !== 'mercadopago') {
      mpInitializedRef.current = false;
      setMpInitPoint(null);
      setMpError(null);
      return;
    }
    if (!mpInitPoint && !mpLoading && !mpError && !mpInitializedRef.current) {
      handleMercadoPago();
    }
  }, [selectedPaymentMethod, mpInitPoint, mpLoading, mpError, handleMercadoPago]);
  
  // Auto-initialize Ualá payment when selected
  useEffect(() => {
    if (selectedPaymentMethod !== 'uala') {
      setUalaPaymentLink(null);
      setUalaError(null);
      return;
    }
    if (!ualaPaymentLink && !ualaLoading && !ualaError) {
      handleUala();
    }
  }, [selectedPaymentMethod, ualaPaymentLink, ualaLoading, ualaError, handleUala]);
  
  useEffect(() => {
    if (!isOpen) return;
    recalculate();
    const timeoutId = setTimeout(recalculate, 50);
    return () => clearTimeout(timeoutId);
  }, [isOpen, recalculate]);

  // Click fuera para cerrar - usando hook reutilizable
  useClickOutsideHandler({
    isOpen,
    sidebarRef,
    handleClose,
  });
  
  const handleConfirm = async () => {
    const finalCbu = selectedBuyerAccount?.cbu || buyerCbu.trim();
    const finalBankName = selectedBuyerAccount?.bank_name || buyerBankName.trim();
    const finalAccountHolder = selectedBuyerAccount?.account_holder || buyerAccountHolder.trim();
    
    if (!finalCbu) {
      toast({
        title: 'CBU requerido',
        description: 'Por favor ingresa tu CBU para que la tienda pueda identificar tu pago.',
        variant: 'destructive',
      });
      return;
    }
    
    if (!finalBankName) {
      toast({
        title: 'Banco requerido',
        description: 'Por favor ingresa el nombre de tu banco.',
        variant: 'destructive',
      });
      return;
    }

    if (isMinibid && deliveryEnabled && isMinibidDeliveryUnavailableByDrivers) {
      toast({
        title: 'Envío por Minibid no disponible',
        description: 'No hay repartidores conectados en este momento. Elegí "Acordar envío con vendedor".',
        variant: 'destructive',
      });
      return;
    }

    if (isMinibid && deliveryEnabled && transportType === 'light' && isMinibidLightUnavailableByDistance) {
      toast({
        title: 'Bici/Moto no disponible',
        description: `Para Bici/Moto la distancia máxima es ${MINIBID_MAX_LIGHT_DELIVERY_KM} km. Elegí Auto o Camioneta.`,
        variant: 'destructive',
      });
      return;
    }
    
    setLoading(true);
    try {
      const isPickupInBranch = !deliveryEnabled && !arrangeWithSeller;

      const normalizedNotes = (orderNotes || '').trim();
      const sellerArrangementNote = 'Envío acordado con vendedor.';
      const digitalEmailNote = isDigital && !deliveryEnabled && !arrangeWithSeller
        ? `Entrega digital por correo: ${userEmail || 'sin email registrado'}.`
        : '';
      const notesWithSeller = arrangeWithSeller
        ? (normalizedNotes.includes(sellerArrangementNote)
            ? normalizedNotes
            : [normalizedNotes, sellerArrangementNote].filter(Boolean).join('\n\n'))
        : normalizedNotes;
      const finalOrderNotes = [notesWithSeller, digitalEmailNote].filter(Boolean).join('\n\n');

      const deliveryPayload = isPickupInBranch
        ? {
            delivery_fee: 0,
            delivery_distance: 0,
            delivery_pickup_address: null,
            delivery_address: null,
            delivery_transport_type: null,
            delivery_pickup_latitude: null,
            delivery_pickup_longitude: null,
            delivery_latitude: null,
            delivery_longitude: null,
          }
        : arrangeWithSeller
        ? {
            delivery_fee: null,
            delivery_distance: deliveryDistance,
            delivery_pickup_address: deliveryRouteInfo?.from || null,
            delivery_address: deliveryRouteInfo?.to || null,
            delivery_transport_type: 'coordinar_vendedor',
            delivery_pickup_latitude: deliveryPickupCoords?.lat ?? null,
            delivery_pickup_longitude: deliveryPickupCoords?.lng ?? null,
            delivery_latitude: deliveryCoords?.lat ?? null,
            delivery_longitude: deliveryCoords?.lng ?? null,
          }
        : {
            delivery_fee: deliveryFee,
            delivery_distance: deliveryDistance,
            delivery_pickup_address: deliveryRouteInfo?.from || null,
            delivery_address: deliveryRouteInfo?.to || null,
            delivery_transport_type: transportType,
            // NUEVO: coordenadas
            delivery_pickup_latitude: deliveryPickupCoords?.lat ?? null,
            delivery_pickup_longitude: deliveryPickupCoords?.lng ?? null,
            delivery_latitude: deliveryCoords?.lat ?? null,
            delivery_longitude: deliveryCoords?.lng ?? null,
          };

      if (showAddForm && saveBuyerInfo && userId && buyerCbu.trim()) {
        const newAccount: BankAccount = {
          cbu: buyerCbu.trim(),
          bank_name: buyerBankName.trim(),
          account_holder: buyerAccountHolder.trim() || '',
          primary: buyerBankAccounts.length === 0
        };
        
        const updatedAccounts = [...buyerBankAccounts, newAccount];
        
        await supabase
          .from('profiles')
          .update({
            bank_accounts: updatedAccounts,
            bank_cbu: buyerCbu.trim(),
            bank_name: buyerBankName.trim(),
            bank_account_holder: buyerAccountHolder.trim() || null,
          })
          .eq('id', userId);
      }
      
      await onConfirm(finalCbu, finalBankName, finalAccountHolder || undefined, finalOrderNotes, selectedPaymentMethod, deliveryPayload, proofPhotoUrl || undefined);
    } catch (e) {
      toast({
        title: 'Error',
        description: (e as any)?.message || 'No se pudo confirmar el pedido.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: `${label} copiado` });
  };

  const handleChangeDeliveryAddress = useCallback((event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    window.dispatchEvent(new CustomEvent('payment-open-address-manager'));
    handleClose();
    window.setTimeout(() => {
      openAddressManager({ forceOpen: true });
    }, 220);
  }, [handleClose, openAddressManager]);

  const handleNativeReceiptChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    if (!selectedFile) return;

    setProofPhotoFile(selectedFile);
    setUploadingProof(true);

    try {
      // Preview local
      const reader = new FileReader();
      reader.onload = (e) => {
        setProofPhotoUrl(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);

      // Upload a Supabase Storage
      const safeFileName = selectedFile.name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '');
      const fileName = `payment_proofs/${userId}/${Date.now()}_${safeFileName}`;
      const { data, error } = await supabase.storage
        .from('minibid-uploads')
        .upload(fileName, selectedFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        toast({
          title: '❌ Error al subir comprobante',
          description: error.message,
          variant: 'destructive',
        });
        setUploadingProof(false);
        return;
      }

      // Obtener URL pública
      const { data: publicData } = supabase.storage
        .from('minibid-uploads')
        .getPublicUrl(fileName);

      if (publicData?.publicUrl) {
        setProofPhotoUrl(publicData.publicUrl);
      }

      setUploadingProof(false);
    } catch (err: any) {
      console.error('Error uploading proof:', err);
      toast({
        title: '❌ Error',
        description: 'No se pudo subir el comprobante',
        variant: 'destructive',
      });
      setUploadingProof(false);
    }
  };

  const handleCameraClick = () => {
    if (receiptInputRef.current) {
      receiptInputRef.current.setAttribute('capture', 'environment');
      receiptInputRef.current.click();
    }
  };

  const handleGalleryClick = () => {
    if (receiptInputRef.current) {
      receiptInputRef.current.removeAttribute('capture');
      receiptInputRef.current.click();
    }
  };

  const getConfirmSliderMaxX = useCallback(() => {
    const track = confirmSliderTrackRef.current;
    if (!track) return 0;
    const handleWidth = confirmSliderHandleRef.current?.clientWidth ?? Math.min(
      Math.max(Math.round(track.clientWidth * PAYMENT_CONFIRM_SLIDER_WIDTH_RATIO), PAYMENT_CONFIRM_SLIDER_MIN_WIDTH),
      PAYMENT_CONFIRM_SLIDER_MAX_WIDTH
    );
    const max = track.clientWidth - handleWidth - PAYMENT_CONFIRM_SLIDER_TRACK_INSET * 2;
    return Math.max(max, 0);
  }, []);

  const resetConfirmSlider = useCallback(() => {
    setConfirmSliderX(0);
    setIsInteractingWithConfirmSlider(false);
    confirmSliderDragRef.current = {
      pointerId: null,
      startClientX: 0,
      startSliderX: 0,
      maxX: 0,
    };
  }, []);

  const isConfirmSliderDisabled = selectedPaymentMethod !== 'bank_transfer' || !proofPhotoUrl || loading || uploadingProof;
  const confirmSliderCopy = proofPhotoUrl ? 'Deslizá para confirmar' : `Cargar comprobante ${formatCurrency(total)}`;
  const isConfirmSliderReady = !isConfirmSliderDisabled && getConfirmSliderMaxX() > 0 && confirmSliderX >= getConfirmSliderMaxX() * PAYMENT_CONFIRM_SLIDER_ACTIVATE_RATIO;

  const handleConfirmSliderPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (isConfirmSliderDisabled) return;

    event.stopPropagation();
    event.preventDefault();

    const maxX = getConfirmSliderMaxX();
    confirmSliderDragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startSliderX: confirmSliderX,
      maxX,
    };

    setIsInteractingWithConfirmSlider(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [confirmSliderX, getConfirmSliderMaxX, isConfirmSliderDisabled]);

  const handleConfirmSliderPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = confirmSliderDragRef.current;
    if (drag.pointerId !== event.pointerId) return;

    event.stopPropagation();
    const delta = event.clientX - drag.startClientX;
    const nextX = Math.min(Math.max(drag.startSliderX + delta, 0), drag.maxX);
    setConfirmSliderX(nextX);
  }, []);

  const finishConfirmSliderDrag = useCallback(async (pointerId: number | null) => {
    const drag = confirmSliderDragRef.current;
    if (drag.pointerId === null || (pointerId !== null && pointerId !== drag.pointerId)) {
      return;
    }

    const maxX = drag.maxX || getConfirmSliderMaxX();
    const shouldActivate = !isConfirmSliderDisabled && maxX > 0 && confirmSliderX >= maxX * PAYMENT_CONFIRM_SLIDER_ACTIVATE_RATIO;

    confirmSliderDragRef.current.pointerId = null;
    setIsInteractingWithConfirmSlider(false);

    if (!shouldActivate) {
      setConfirmSliderX(0);
      return;
    }

    setConfirmSliderX(maxX);
    await handleConfirm();
  }, [confirmSliderX, getConfirmSliderMaxX, handleConfirm, isConfirmSliderDisabled]);

  const handleConfirmSliderPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    void finishConfirmSliderDrag(event.pointerId);
  }, [finishConfirmSliderDrag]);

  const handleConfirmSliderPointerCancel = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    void finishConfirmSliderDrag(event.pointerId);
  }, [finishConfirmSliderDrag]);

  useEffect(() => {
    if (selectedPaymentMethod !== 'bank_transfer') {
      resetConfirmSlider();
    }
  }, [selectedPaymentMethod, resetConfirmSlider]);

  useEffect(() => {
    if (!proofPhotoUrl || uploadingProof) {
      resetConfirmSlider();
    }
  }, [proofPhotoUrl, resetConfirmSlider, uploadingProof]);
  
  const showDeliveryMethodSkeleton = deliveryLoading || (!isDeliveryCalculated && !deliveryError);
  const selectionSkeletonWidths = [
    { title: 'w-40', subtitle: 'w-56' },
    { title: 'w-44', subtitle: 'w-64' },
    { title: 'w-36', subtitle: 'w-60' },
  ];

  const renderSelectionSkeleton = (keyPrefix: string) => (
    <div className="space-y-2">
      {selectionSkeletonWidths.map((row, idx) => (
        <div
          key={`${keyPrefix}-${idx}`}
          className="w-full rounded-xl border border-border/50 bg-gradient-to-r from-muted/25 via-muted/35 to-muted/25 p-3"
        >
          <div className="flex items-start gap-3">
            <Skeleton className="mt-0.5 h-5 w-5 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className={`h-4 ${row.title}`} />
              <Skeleton className={`h-3 ${row.subtitle}`} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
  
  return (
    <AnimatePresence>
      {(isOpen || isClosing) && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-transparent z-[9998]"
            onClick={onClose}
          />

          {/* Sidebar */}
          <motion.div
            ref={sidebarRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.2 }}
            className={cn(
              'fixed right-0 w-full max-w-[100vw] lg:w-[400px] bg-background shadow-2xl z-[9999] flex min-w-0 flex-col overflow-x-hidden'
            )}
            style={{
              top: isDesktop ? `${headerHeight}px` : '0',
              bottom: !isDesktop ? 0 : undefined,
              height: isDesktop ? `calc(100vh - ${headerHeight}px)` : '100dvh',
              pointerEvents: (isOpen && !isClosing) ? 'auto' : 'none'
            }}
          >
      {/* Safe Area Spacer for Mobile */}
      {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}
      <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px] gap-3 min-w-0">
        <h2 className="text-lg font-semibold flex items-center gap-2 min-w-0 flex-1">
          {selectedPaymentMethod === 'mercadopago' ? (
            <>
              <WalletCards className="w-5 h-5 text-primary-foreground" />
              <span className="min-w-0 break-words">Pago con Mercado Pago</span>
            </>
          ) : selectedPaymentMethod === 'uala' ? (
            <>
              <WalletCards className="w-5 h-5 text-primary-foreground" />
              <span className="min-w-0 break-words">Pago con Ualá</span>
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5 text-primary-foreground" />
              <span className="min-w-0 break-words">Pago</span>
            </>
          )}
        </h2>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground" 
                onClick={(e) => { e.stopPropagation(); handleClose(); }}
              >
                <X className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent variant="sidebar">
              <p>Cerrar</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <ScrollArea className="flex-1 overflow-x-hidden" ref={scrollAreaRef}>
        <div className="p-4 space-y-6 min-w-0 max-w-full overflow-x-hidden">
          {/* Total Summary Card */}
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20 rounded-xl p-4 space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total a pagar</p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-primary">{formatCurrency(total)}</span>
              {deliveryFee && deliveryEnabled && (
                <span className="text-xs text-muted-foreground">+ {formatCurrency(deliveryFee)} envío</span>
              )}
            </div>
          </div>
          
          {loadingStore ? (
            <>
              <div className="p-3 sm:p-4 bg-muted/50 rounded-lg space-y-3">
                <Skeleton className="h-5 w-32" />
                <div className="space-y-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <label className="font-medium text-sm" htmlFor="orderNotes">Notas para la tienda (opcional)</label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-[11px]"
                      onClick={handlePastePreviousOrderNote}
                    >
                      Pegar nota anterior
                    </Button>
                  </div>
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
              <Skeleton className="h-10 w-full" />
            </>
          ) : storeBankAccounts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                La tienda {storeName} no tiene datos bancarios configurados.
              </p>
            </div>
          ) : (
            <>
              {/* Delivery Section */}
              <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-primary" />
                      <h3 className="text-sm font-semibold">Método de entrega</h3>
                    </div>
                    {showDeliveryMethodSkeleton ? (
                      renderSelectionSkeleton('delivery')
                    ) : (
                    <>
                    <div className="space-y-2">
                      {/* Retiro Personalmente - Hidden for minibid */}
                      {(!isMinibid || isDigital) && (
                        <button
                        type="button"
                        onClick={() => {
                          setDeliveryEnabled(false);
                          setArrangeWithSeller(false);
                        }}
                        className={cn(
                          'w-full p-3 border-2 rounded-lg transition-all flex items-start gap-3',
                          !deliveryEnabled && !arrangeWithSeller
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        )}
                      >
                        <div className={cn(
                          'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
                          !deliveryEnabled && !arrangeWithSeller
                            ? 'border-primary bg-primary'
                            : 'border-muted-foreground/30'
                        )}>
                          {!deliveryEnabled && !arrangeWithSeller && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <div className="text-left flex-1">
                          <div className="font-semibold text-sm">{isDigital ? 'Recibir en mi correo electrónico' : 'Retiro personalmente'}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {isDigital
                              ? (userEmail ? `Se enviará a: ${userEmail}` : 'No encontramos email en tu perfil.')
                              : 'Recogé en la sucursal'}
                          </div>
                        </div>
                      </button>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          setDeliveryEnabled(false);
                          setArrangeWithSeller(true);
                        }}
                        className={cn(
                          'w-full p-3 border-2 rounded-lg transition-all flex items-start gap-3',
                          arrangeWithSeller
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        )}
                      >
                        <div className={cn(
                          'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
                          arrangeWithSeller
                            ? 'border-primary bg-primary'
                            : 'border-muted-foreground/30'
                        )}>
                          {arrangeWithSeller && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <div className="font-semibold text-sm">Acordar entrega con vendedor</div>
                          <div className="text-xs text-muted-foreground mt-0.5 break-words">
                            Coordinas costo y modalidad directamente con la tienda.
                          </div>
                        </div>
                      </button>

                    {/* Agregar Envío - Only show when calculated */}
                        {isDeliveryCalculated && !deliveryError ? (
                          <div
                            role="button"
                            tabIndex={isMinibidDeliveryUnavailable ? -1 : 0}
                            aria-disabled={isMinibidDeliveryUnavailable}
                            className={cn(
                              'w-full p-3 border-2 rounded-lg transition-all flex items-start gap-3',
                              isMinibidDeliveryUnavailable && 'opacity-60 cursor-not-allowed border-dashed',
                              !isMinibidDeliveryUnavailable && 'cursor-pointer',
                              deliveryEnabled
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50'
                            )}
                            onClick={() => {
                              if (isMinibidDeliveryUnavailable) return;
                              setDeliveryEnabled(true);
                              setArrangeWithSeller(false);
                            }}
                            onKeyDown={(e) => {
                              if (isMinibidDeliveryUnavailable) return;
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setDeliveryEnabled(true);
                                setArrangeWithSeller(false);
                              }
                            }}
                          >
                            <div className={cn(
                              'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
                              deliveryEnabled && !arrangeWithSeller
                                ? 'border-primary bg-primary'
                                : 'border-muted-foreground/30'
                            )}>
                              {deliveryEnabled && !arrangeWithSeller && (
                                <div className="w-2 h-2 bg-white rounded-full" />
                              )}
                            </div>
                            <div className="text-left flex-1 min-w-0">
                              <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                                <span className="break-words">Envío por Minibid</span>
                                {isFreeShipping && (
                                  <span className="px-2 py-0.5 bg-green-500/20 text-green-700 text-xs font-bold rounded-full border border-green-500/30">
                                    ¡Gratis!
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5 break-words">
                                {isMinibidDeliveryUnavailableByDrivers && (
                                  <div className="text-amber-700 font-semibold">
                                    No disponible: no hay repartidores conectados en este momento.
                                  </div>
                                )}
                                {isMinibidLightUnavailableByDistance && (
                                  <div className="text-amber-700 font-semibold">
                                    Bici/Moto no disponible cuando la distancia supera {MINIBID_MAX_LIGHT_DELIVERY_KM} km.
                                  </div>
                                )}
                                {deliveryFee !== null && (
                                  <div className="block text-left">
                                    {isFreeShipping ? (
                                      <div className="text-green-700 font-semibold">✓ Envío gratis</div>
                                    ) : (
                                      <div className="break-words">Pagás <span className="font-semibold">{formatCurrency(deliveryFee)}</span> al recibir</div>
                                    )}
                                    {deliveryRouteInfo && (
                                      <div className="mt-1 opacity-80 text-muted-foreground [overflow-wrap:anywhere]">
                                        <MapPin className="inline h-3 w-3 mr-1" />
                                        {deliveryRouteInfo.from} → {deliveryRouteInfo.to}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {isMinibid && (
                                  <div className="mt-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={handleChangeDeliveryAddress}
                                      className="h-7 px-2 text-[11px] border-primary/30 bg-primary/5 text-primary hover:bg-primary/10"
                                    >
                                      Cambiar direccion
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : null}
                  </div>
                  
                  {/* Transport Options - Hide when user selects pickup */}
                  {transportOptions.length > 0 && deliveryEnabled && !showDeliveryMethodSkeleton && (
                    <div className="space-y-3 pt-2">
                      {/* DOCUMENTATION: Changed label and styling to match RequestRideSidebar - now uses Label component */}
                      <Label className="text-sm font-semibold block">Repartidor disponible</Label>
                      <div className="space-y-2">
                        {/* DOCUMENTATION: Changed to show only light/bike option */}
                        {transportOptions.filter(option => option.type === 'light').map(option => {
                          const Icon = option.icon;
                          const isFree = option.fee === 0;
                          const isLoading = option.isLoading;
                          const isLightOptionUnavailable = option.type === 'light' && isMinibidLightUnavailableByDistance;
                          return (
                            <button
                              key={option.type}
                              onClick={() => {
                                if (isLightOptionUnavailable) return;
                                setTransportType(option.type as any);
                                setDeliveryFee(option.fee);
                              }}
                              disabled={isLoading || isLightOptionUnavailable}
                              className={cn(
                                'w-full p-3 border-2 rounded-lg transition-all flex items-center gap-3',
                                (isLoading || isLightOptionUnavailable) && 'opacity-60 cursor-not-allowed',
                                !isLoading && (transportType === option.type
                                  ? 'border-primary bg-primary/5'
                                  : 'border-border hover:border-primary/50')
                              )}
                            >
                              <div className={cn(
                                'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                                transportType === option.type ? 'border-primary bg-primary' : 'border-muted-foreground/30'
                              )}>
                                {transportType === option.type && <div className="w-2 h-2 bg-white rounded-full" />}
                              </div>
                              <div className="flex-shrink-0">
                                {option.type === 'light' ? (
                                  <div className="flex items-center gap-1">
                                    <Bike className={cn('w-4 h-4', transportType === option.type ? 'text-primary' : 'text-muted-foreground')} />
                                    <Motorbike className={cn('w-4 h-4', transportType === option.type ? 'text-primary' : 'text-muted-foreground')} />
                                  </div>
                                ) : (
                                  <Icon className={cn('w-5 h-5', transportType === option.type ? 'text-primary' : 'text-muted-foreground')} />
                                )}
                              </div>
                              <div className="flex-1 flex justify-between items-center text-left">
                                <div className="font-semibold text-sm">{option.label}</div>
                                <div className={cn(
                                  "text-sm font-bold flex items-center gap-1",
                                  isFree && !isLoading ? "text-green-600" : "text-primary"
                                )}>
                                  {isLightOptionUnavailable ? (
                                    <span className="text-xs text-amber-700 font-semibold">No disponible {'>'}{MINIBID_MAX_LIGHT_DELIVERY_KM} km</span>
                                  ) : isLoading ? (
                                    <span className="text-xs text-muted-foreground">Calculando...</span>
                                  ) : isFree ? (
                                    <>
                                      <span className="px-2 py-0.5 bg-green-500/20 text-green-700 text-xs font-bold rounded-full border border-green-500/30">
                                        Gratis
                                      </span>
                                    </>
                                  ) : (
                                    formatCurrency(option.fee)
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                    </>
                  )}
              </div>

              {/* Payment Method Section */}
              <div className="space-y-3 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <WalletCards className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold">
                    {loadingPaymentMethods ? 'Elegí cómo pagar' : (enabledPaymentMethods.length === 1 ? 'Medio de pago' : 'Elegí cómo pagar')}
                  </h3>
                </div>
                <div className="space-y-2">
                  {loadingPaymentMethods ? (
                    renderSelectionSkeleton('payment')
                  ) : paymentPlatforms.filter(p => enabledPaymentMethods.includes(p.key)).map(platform => (
                    <button
                      key={platform.key}
                      type="button"
                      className={cn(
                        'w-full p-3 border-2 rounded-lg transition-all flex items-start gap-3',
                        selectedPaymentMethod === platform.key
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      )}
                      onClick={() => setSelectedPaymentMethod(platform.key)}
                    >
                      <div className={cn(
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
                        selectedPaymentMethod === platform.key
                          ? 'border-primary bg-primary'
                          : 'border-muted-foreground/30'
                      )}>
                        {selectedPaymentMethod === platform.key && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div className="text-left flex-1 min-w-0">
                        <div className="font-semibold text-sm">{platform.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5 break-words">
                          {platform.key === 'bank_transfer' && 'CBU o alias disponible para transferencia'}
                          {platform.key === 'mercadopago' && 'Tarjeta de crédito, débito o billetera'}
                          {platform.key === 'uala' && 'Billetera digital Ualá'}
                          {platform.key === 'naranjax' && 'Tarjeta Naranja X'}
                          {platform.key === 'mobbex' && 'Múltiples opciones de pago'}
                        </div>

                        {platform.key === 'bank_transfer' && selectedPaymentMethod === 'bank_transfer' && (
                          <div className="mt-3 space-y-3 rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 p-2.5 sm:p-3 max-[360px]:mt-2.5 max-[360px]:space-y-2 max-[360px]:p-2">
                            <div className="space-y-0.5">
                              <div className="text-xs sm:text-sm text-foreground/90 break-words max-[360px]:text-[11px]">
                                Transferi <span className="text-base align-baseline font-bold text-primary">{formatCurrency(total)}</span> a la cuenta de {storeName}
                              </div>
                            </div>

                            <div className="rounded-xl border border-primary/20 bg-background/80 p-2.5 sm:p-3 space-y-2.5 shadow-sm max-[360px]:p-2 max-[360px]:space-y-2">
                              <h4 className="text-[11px] font-black uppercase tracking-wider text-primary max-[360px]:text-[10px]">Datos de la tienda</h4>
                              {selectedAccount && (
                                <div className="space-y-2 text-sm">
                                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                                    <span className="text-muted-foreground font-semibold uppercase text-[11px] max-[360px]:text-[10px]">CBU:</span>
                                    <div className="flex flex-wrap items-center gap-1.5 min-w-0 max-w-full sm:justify-end">
                                      <span className="text-[11px] sm:text-xs break-all font-semibold text-foreground min-w-0 [overflow-wrap:anywhere] max-[360px]:text-[10px]">{selectedAccount.cbu}</span>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-6 px-2 text-[11px] border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 shrink-0 max-[360px]:h-5 max-[360px]:px-1.5 max-[360px]:text-[10px]"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          copyToClipboard(selectedAccount.cbu, 'CBU');
                                        }}
                                      >
                                        Copiar
                                      </Button>
                                    </div>
                                  </div>
                                  {selectedAccount.alias && (
                                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                                      <span className="text-muted-foreground font-semibold uppercase text-[11px] max-[360px]:text-[10px]">Alias:</span>
                                      <div className="flex flex-wrap items-center gap-1.5 min-w-0 max-w-full sm:justify-end">
                                        <span className="text-[11px] sm:text-xs break-all font-semibold text-foreground min-w-0 [overflow-wrap:anywhere] max-[360px]:text-[10px]">{selectedAccount.alias}</span>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="h-6 px-2 text-[11px] border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 shrink-0 max-[360px]:h-5 max-[360px]:px-1.5 max-[360px]:text-[10px]"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            copyToClipboard(selectedAccount.alias!, 'Alias');
                                          }}
                                        >
                                          Copiar
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                                    <span className="text-muted-foreground font-semibold uppercase text-[11px]">Banco:</span>
                                    <span className="break-words font-medium">{selectedAccount.bank_name}</span>
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                                    <span className="text-muted-foreground font-semibold uppercase text-[11px]">Titular:</span>
                                    <span className="break-words font-medium">{selectedAccount.account_holder}</span>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="space-y-2.5 rounded-xl border border-primary/20 bg-background/80 p-2.5 sm:p-3 shadow-sm max-[360px]:p-2 max-[360px]:space-y-2">
                              <h4 className="text-[11px] font-black uppercase tracking-wider text-primary max-[360px]:text-[10px]">Tus datos bancarios</h4>
                              <p className="text-xs text-foreground/70 leading-relaxed">
                                Ingresa tu CBU para que la tienda pueda identificar tu transferencia.
                              </p>

                              {buyerBankAccounts.length > 0 && !showAddForm && (
                                <div className="space-y-2">
                                  {buyerBankAccounts.map((account, idx) => (
                                    <div
                                      key={account.cbu + idx}
                                      className={`p-2.5 rounded-lg cursor-pointer transition-colors border-2 max-[360px]:p-2 ${
                                        selectedBuyerAccount?.cbu === account.cbu
                                          ? 'border-primary bg-primary/10 shadow-sm'
                                          : 'border-primary/10 bg-background/80 hover:bg-primary/5'
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedBuyerAccount(account);
                                      }}
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                          <div className="text-sm break-all font-semibold [overflow-wrap:anywhere] max-[360px]:text-xs">{account.cbu}</div>
                                          <div className="text-xs text-foreground/65 mt-0.5 break-words">
                                            {account.bank_name}{account.account_holder ? ` • ${account.account_holder}` : ''}
                                          </div>
                                        </div>
                                        {selectedBuyerAccount?.cbu === account.cbu && (
                                          <Check className="h-5 w-5 text-primary flex-shrink-0" />
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full mt-2 border-primary/30 bg-primary/5 text-primary hover:bg-primary/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedBuyerAccount(null);
                                      setShowAddForm(true);
                                      setBuyerCbu('');
                                      setBuyerBankName('');
                                      setBuyerAccountHolder('');
                                    }}
                                  >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Agregar otra cuenta
                                  </Button>
                                </div>
                              )}

                              {showAddForm && (
                                <div className="space-y-3">
                                  {buyerBankAccounts.length > 0 && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-xs"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowAddForm(false);
                                        const primary = buyerBankAccounts.find(a => a.primary);
                                        setSelectedBuyerAccount(primary || buyerBankAccounts[0]);
                                      }}
                                    >
                                      ← Usar cuenta guardada
                                    </Button>
                                  )}
                                  <div>
                                    <Label htmlFor="buyerCbu" className="text-sm">CBU / Alias *</Label>
                                    <Input
                                      id="buyerCbu"
                                      value={buyerCbu}
                                      onChange={(e) => setBuyerCbu(e.target.value)}
                                      placeholder="0000000000000000000000"
                                      maxLength={22}
                                      className="mt-1"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="buyerBank" className="text-sm">Banco *</Label>
                                    <Input
                                      id="buyerBank"
                                      value={buyerBankName}
                                      onChange={(e) => setBuyerBankName(e.target.value)}
                                      placeholder="Ej: Banco Nación, Mercado Pago"
                                      className="mt-1"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="buyerHolder" className="text-sm">Titular (opcional)</Label>
                                    <Input
                                      id="buyerHolder"
                                      value={buyerAccountHolder}
                                      onChange={(e) => setBuyerAccountHolder(e.target.value)}
                                      placeholder="Nombre del titular"
                                      className="mt-1"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                  <label
                                    className="flex items-start gap-2 text-sm cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Checkbox
                                      checked={saveBuyerInfo}
                                      onCheckedChange={v => setSaveBuyerInfo(v === true)}
                                      className="mt-0.5"
                                    />
                                    <span className="leading-tight">Guardar esta cuenta para futuros pedidos</span>
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {platform.key !== 'bank_transfer' && platform.key !== 'mercadopago' && platform.key !== 'uala' && selectedPaymentMethod === platform.key && (
                          <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-muted-foreground">
                            Vas a pagar con <span className="font-semibold" style={{ color: platform.color }}>{platform.name}</span>.
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <label className="font-medium text-sm" htmlFor="orderNotes">Notas para la tienda (opcional)</label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handlePastePreviousOrderNote}
                    disabled={isLoadingPreviousOrderNote}
                    className="h-7 px-2 text-xs"
                  >
                    {isLoadingPreviousOrderNote ? 'Cargando...' : 'Pegar nota anterior'}
                  </Button>
                </div>
                <input
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  id="orderNotes"
                  placeholder="Ej: Aclaraciones, referencia, etc."
                  maxLength={200}
                  value={orderNotes}
                  onChange={e => setOrderNotes(e.target.value)}
                />
              </div>
              
            </>
          )}
        </div>
      </ScrollArea>
      
      {!loadingStore && storeBankAccounts.length > 0 && (
        <div className="border-t p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] space-y-3 shrink-0 max-[360px]:p-3 max-[360px]:space-y-2.5">
          {/* Payment Proof Section - Only for bank_transfer */}
          {selectedPaymentMethod === 'bank_transfer' && (
            <div className="bg-muted/50 rounded-lg p-3 border border-primary/20 space-y-2 max-[360px]:p-2 max-[360px]:space-y-1.5">
              <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground max-[360px]:text-xs">
                <Camera className="w-4 h-4" />
                Comprobante de transferencia
              </div>
              
              {/* Preview del comprobante */}
              {proofPhotoUrl && (
                <div className="relative w-full h-24 rounded-lg bg-black/5 overflow-hidden border border-primary/30">
                  <img 
                    src={proofPhotoUrl} 
                    alt="Comprobante" 
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => {
                      setProofPhotoUrl(null);
                      setProofPhotoFile(null);
                    }}
                    className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/80 hover:bg-red-600 text-white flex items-center justify-center shadow-md"
                    aria-label="Remover comprobante"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              
              {/* Botones cámara + galería */}
              <div className="flex flex-wrap gap-2">
                <Button
                  ref={cameraButtonRef}
                  onClick={handleCameraClick}
                  disabled={uploadingProof}
                  variant="outline"
                  className="flex-1 min-w-[8.5rem] sm:min-w-0 h-9 text-xs font-semibold flex items-center justify-center gap-2 max-[360px]:min-w-full"
                >
                  <Camera className="w-3 h-3" />
                  {uploadingProof ? 'Subiendo...' : 'Cámara'}
                </Button>
                <Button
                  ref={galleryButtonRef}
                  onClick={handleGalleryClick}
                  disabled={uploadingProof}
                  variant="outline"
                  className="flex-1 min-w-[8.5rem] sm:min-w-0 h-9 text-xs font-semibold flex items-center justify-center gap-2 max-[360px]:min-w-full"
                >
                  <ImageIcon className="w-3 h-3" />
                  {uploadingProof ? 'Subiendo...' : 'Galería'}
                </Button>
              </div>
              
              {proofPhotoUrl && (
                <p className="hidden">
                  \u2713 Comprobante cargado correctamente
                </p>
              )}
            </div>
          )}
          
          <input
            ref={receiptInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleNativeReceiptChange}
          />
          {selectedPaymentMethod === 'bank_transfer' ? (
            <div
              ref={confirmSliderTrackRef}
              className={cn(
                'relative h-11 w-full rounded-full border border-primary/10 overflow-hidden',
                isConfirmSliderDisabled ? 'bg-card/60 opacity-70' : 'bg-card/60'
              )}
            >
              <div
                className="absolute rounded-full bg-primary"
                style={{
                  left: `${PAYMENT_CONFIRM_SLIDER_TRACK_INSET}px`,
                  top: `${PAYMENT_CONFIRM_SLIDER_TRACK_INSET}px`,
                  bottom: `${PAYMENT_CONFIRM_SLIDER_TRACK_INSET}px`,
                  width: `calc(${confirmSliderX}px + ${PAYMENT_CONFIRM_SLIDER_HANDLE_WIDTH_CSS})`,
                  transition: isInteractingWithConfirmSlider
                    ? 'none'
                    : `width ${PAYMENT_CONFIRM_SLIDER_FILL_SNAP_MS}ms ${PAYMENT_CONFIRM_SLIDER_FILL_SNAP_EASING}`,
                }}
              />

              <p className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-end pr-4 text-right text-[11px] font-semibold tracking-wide text-muted-foreground transition-opacity duration-200">
                {confirmSliderCopy}
              </p>

              <div
                role="button"
                aria-label={confirmSliderCopy}
                aria-disabled={isConfirmSliderDisabled}
                tabIndex={-1}
                onPointerDown={handleConfirmSliderPointerDown}
                onPointerMove={handleConfirmSliderPointerMove}
                onPointerUp={handleConfirmSliderPointerUp}
                onPointerCancel={handleConfirmSliderPointerCancel}
                className={cn(
                  'absolute z-10 h-10 rounded-full border shadow-sm overflow-hidden',
                  'grid place-items-center touch-none select-none transition-transform',
                  isConfirmSliderDisabled
                    ? 'border-primary/20 bg-primary/70 cursor-not-allowed'
                    : 'border-primary/30 bg-primary active:scale-[0.985]'
                )}
                ref={confirmSliderHandleRef}
                style={{
                  width: PAYMENT_CONFIRM_SLIDER_HANDLE_WIDTH_CSS,
                  left: `${PAYMENT_CONFIRM_SLIDER_TRACK_INSET}px`,
                  top: `${PAYMENT_CONFIRM_SLIDER_TRACK_INSET}px`,
                  transform: `translateX(${confirmSliderX}px)`,
                  transition: isInteractingWithConfirmSlider
                    ? 'none'
                    : `transform ${PAYMENT_CONFIRM_SLIDER_HANDLE_SNAP_MS}ms ${PAYMENT_CONFIRM_SLIDER_HANDLE_SNAP_EASING}`,
                }}
              >
                {loading || uploadingProof ? (
                  <Loader2 className="h-4 w-4 text-primary-foreground animate-spin" />
                ) : isConfirmSliderReady ? (
                  <Check className="h-4 w-4 text-primary-foreground" />
                ) : (
                  <ArrowRight className="h-4 w-4 text-primary-foreground animate-[bounce-x_2s_ease-in-out_infinite]" />
                )}
              </div>
            </div>
          ) : (
            <Button 
              onClick={async () => {
                if (selectedPaymentMethod === 'mercadopago') {
                  if (!mpInitPoint) await handleMercadoPago();
                  if (mpInitPoint) window.open(mpInitPoint, '_self');
                } else if (selectedPaymentMethod === 'uala') {
                  if (!ualaPaymentLink) await handleUala();
                  if (ualaPaymentLink) window.open(ualaPaymentLink, '_self');
                } else {
                  handleConfirm();
                }
              }} 
              disabled={loading || mpLoading || ualaLoading || uploadingProof || (selectedPaymentMethod === 'mercadopago' && !mpInitPoint && !mpLoading) || (selectedPaymentMethod === 'uala' && !ualaPaymentLink && !ualaLoading)} 
              className={selectedPaymentMethod === 'mercadopago' ? 'w-full min-h-11 h-auto py-2 bg-mercadopago text-white hover:bg-mercadopago whitespace-normal break-words text-center' : selectedPaymentMethod === 'uala' ? 'w-full min-h-11 h-auto py-2 bg-uala text-white hover:bg-uala whitespace-normal break-words text-center' : 'w-full min-h-11 h-auto py-2 whitespace-normal break-words text-center'}
            >
              {loading || mpLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Procesando...
                </>
              ) : ualaLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Procesando...
                </>
              ) : selectedPaymentMethod === 'mercadopago' ? (
                `Pagar ${formatCurrency(total)} con Mercado Pago`
              ) : selectedPaymentMethod === 'uala' ? (
                `Pagar ${formatCurrency(total)} con Ualá`
              ) : (
                `Confirmar pago ${formatCurrency(total)}`
              )}
            </Button>
          )}
        </div>
      )}
      {/* Safe Area Spacer for Mobile Bottom - only when no footer */}
      {(loadingStore || storeBankAccounts.length === 0) && !isDesktop && <div className="h-[env(safe-area-inset-bottom,0px)] w-full shrink-0" />}
      </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
